// src/api/authService.ts
import apiClient from './apiClient';
import {
    LoginPayload,
    RegisterPayload,
    LoginResponseData,
    GoogleSignInPayload,
    ResetPasswordPayload,
    RequestPasswordResetPayload,
    ConfirmPasswordResetPayload,
    RequestEmailChangePayload,
    ConfirmEmailChangePayload,
    DisconnectGooglePayload,
    RegisterResponseData,
    GoogleSignInResponseData
} from '../types';

/**
 * Handles user login with email and password.
 */
export const login = (payload: LoginPayload): Promise<LoginResponseData> => {
    return apiClient.post('/api/auth/login', payload);
};

/**
 * Handles new user registration.
 */
export const register = (payload: RegisterPayload): Promise<RegisterResponseData> => {
    return apiClient.post('/api/auth/register', payload);
};

/**
 * Logs the current user out.
 */
export const logout = (): Promise<any> => {
    return apiClient.post('/api/auth/logout');
};

/**
 * Requests a verification code to be sent to the user's email for registration.
 */
export const requestEmailVerification = (email: string): Promise<any> => {
    return apiClient.get(`/api/auth/verify-email?email=${email}`);
};

/**
 * Handles user login/registration via Google OAuth2.
 * Sends the authorization code to the backend for validation.
 */
export const googleSignIn = (payload: GoogleSignInPayload): Promise<GoogleSignInResponseData> => {
    return apiClient.post('/api/auth/google-sign-in', payload);
};

/**
 * Requests a password reset email for a given email address (forgot password flow).
 */
export const requestPasswordReset = (payload: RequestPasswordResetPayload): Promise<any> => {
    return apiClient.post('/api/auth/request-password-reset', payload);
};

/**
 * Confirms the password reset with a new password and token from the URL.
 */
export const confirmPasswordReset = (payload: ConfirmPasswordResetPayload): Promise<any> => {
    return apiClient.post('/api/auth/confirm-password-reset', payload);
};

/**
 * Changes the password for the currently authenticated user (requires current password).
 */
export const resetPasswordWithOldPassword = (payload: ResetPasswordPayload): Promise<any> => {
    return apiClient.post('/api/auth/password-reset', payload);
};

/**
 * Requests a verification code to be sent to the new email address.
 */
export const requestEmailChange = (payload: RequestEmailChangePayload): Promise<any> => {
    return apiClient.post('/api/auth/request-email-change', payload);
};

/**
 * Confirms the email change with the verification code.
 */
export const confirmEmailChange = (payload: ConfirmEmailChangePayload): Promise<any> => {
    return apiClient.post('/api/auth/confirm-email-change', payload);
};

/**
 * Disconnects the user's Google account. A new password may be required.
 */
export const disconnectGoogle = (payload?: DisconnectGooglePayload): Promise<any> => {
    return apiClient.post('/api/auth/disconnect-google', payload);
};

/**
 * Schedules the user's account for deletion.
 */
export const deleteAccount = (): Promise<any> => {
    return apiClient.post('/api/auth/delete-account');
};
